import { Obj, VoidCallback1 } from '@deqo/foundation';
import React, { useState } from 'react';
import AutoSizeInput from 'react-input-autosize';
import styled from 'styled-components';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { Icon } from '../../concepts/resources/Icon';
import { useSubmitter } from '../../platform/Hook';
import { DropButton } from '../../ui/DropButton';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { useResolve } from '../Container';
import { ProfileModel } from '../ProfileModel';
import { Copy, Flexer, Spacer, Title } from '../dashboard/elements/LayoutElements';
import { Colors, Grid, Toggle, remInPx } from './../../ui';
import { OptionControl, OptionInfo } from './PreferencesElements';
import { ProfilePhotoView } from './ProfilePhotoView';

const XAutosizeInput = AutoSizeInput as any;

const DropInputFrame = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    input {
        background: transparent;
        font-size: 1.125rem;
        font-weight: 450;
        color: ${Colors.text};
    }
`;


const InlineEditTextInput = ({ initialValue, onChange }: { initialValue: string, onChange: VoidCallback1<string> }) => {
    const [value, setValue] = useState(initialValue);
    const [dirty, setDirty] = useState(false);

    const onValue = (nextValue: string) => {
        setValue(nextValue);
        setDirty(true);
    };

    const onSave = () => {
        setDirty(false);
        onChange(value);
    };

    const onReset = () => {
        setValue(initialValue);
        setDirty(false);
    };

    const onKeyPress = (e: any) => {
        if (e.key === 'Enter') {
            onSave();
        }
        if (e.key === 'Escape') {
            onReset();
        }
    };

    return <DropButton inline active overflow=".25 -.5">
        <DropInputFrame>
            <XAutosizeInput type="text" autoComplete="false" value={value} minWidth={remInPx(8)} maxLength={128}
                onChange={(e: any) => onValue(e.target.value)}
                onFocus={(e: any) => e.target.select()}
                onKeyPress={onKeyPress}
                onBlur={onSave}
            />
            {dirty && <DropButton>
                <Icon size="1" src="deqo://icons/hero-mini/check" />
            </DropButton>}
        </DropInputFrame>
    </DropButton>;

};

const NameView = React.memo<{ name: string, onChange: VoidCallback1<string> }>(({ name, onChange }) => {
    return <Flexer className="ignore-popup">
        <Copy>Your name:</Copy>
        <InlineEditTextInput initialValue={name} onChange={onChange} />
    </Flexer>;
});

export const ProfileView = () => {

    const api = useResolve(ApiClient);
    const me = useResolve(ProfileModel);
    const [state, setState] = useState(Obj.pick(me.getState(), 'name', 'photo', 'emailSubscribed'));

    const [onPatchProfile, _working1, result] = useSubmitter(async (patch: Partial<typeof state>) => {
        const before = Object.fromEntries(Object.keys(patch).map(x => [x, state[x]]));
        setState(x => ({ ...x, ...patch }));
        try {
            await api.exec(v2.me.update(patch));
        }
        catch (e) {
            setState(x => ({ ...x, ...before }));
            throw e;
        }
    }, [state]);

    return <Flexer width="32" flex="column" gap="1">
        <Flexer flex="row">
            <Flexer shrink padding="0 4 0 0" minWidth="24">
                <NameView name={state.name} onChange={x => onPatchProfile({ name: x })} />
                <Spacer f={1} />
                <Copy>Your email:</Copy>
                <Title>{me.email}</Title>
            </Flexer>
            <Flexer shrink>
                <ProfilePhotoView />
            </Flexer>
        </Flexer>
        <Flexer borderTop={`solid 1px ${Colors.border}`} padding="2 0 0">
            <Grid columns="auto 4rem" columnGap="1rem" rowGap="2rem">
                <OptionInfo>
                    <Title>Receive Marketing Emails</Title>
                    <Copy>
                        Subscribe to product news, updates & tips from Metro Retro (usually sent monthly).
                    </Copy>
                </OptionInfo>
                <OptionControl>
                    <Toggle value={state.emailSubscribed} onAction={x => onPatchProfile({ emailSubscribed: x })} />
                </OptionControl>
            </Grid>
        </Flexer>
        <Flexer margin="2 0">
            <ResultInfoForSubmitter result={result} onSuccess="Changes saved." />
        </Flexer>
    </Flexer>;
};
